import { useCallback, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useMutation } from '@apollo/client';

import CodeEditor from 'components/common/CodeEditor/CodeEditor';
import { queryTimeRangeState, searchParamState } from 'components/common/Managers/RouteStateManager.state';
import Dialog from 'components/common/Dialog/Dialog';
import * as K from 'common/constants';
import { clearGraphQLErrorMessage } from 'common/util/string';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';

import { GraphConfigType } from 'components/dashboards/types';
import { EDIT_DASHBOARD_GRAPH, GET_CONFIGV2 } from 'components/dashboards/queries';
import { trimLayout } from 'components/dashboards/dashboardData';

import './EditGraphJSON.scss';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';

/**
 * Modal dialog for editing a single dashboard graph JSON, in situ.
 *
 * @prop showModal          This modal is shown when true
 * @prop dashboardName      The dashboard config file name
 * @prop graphConfig        The JSON stanza for just this graph
 * @prop graphIndex         The index of this graph in the graphs array stanza
 * @prop handleClose        Callback for when the modal is closed
 *
 */

type EditGraphJSONProps = {
  showModal: boolean;
  graphIndex: number;
  graphConfig: GraphConfigType;
  handleClose: () => void;
};

function EditGraphJSON({ showModal, graphConfig, graphIndex, handleClose }: EditGraphJSONProps): JSX.Element {
  const dashboardName = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_NAME));

  const formatInitialJSON = (): string => {
    const jsonToFormat = { ...graphConfig };
    // We strip the layout before showing and then add it back in when saving (below)
    if (jsonToFormat.layout) {
      delete jsonToFormat.layout;
    }
    return JSON.stringify(jsonToFormat, null, 2);
  };
  const [json, setJson] = useState(formatInitialJSON());

  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const [saveGraphJSON] = useMutation(EDIT_DASHBOARD_GRAPH, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name: dashboardName, startTime, endTime } }],
  });
  const tabName = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_ACTIVE_TAB));
  const [errorMessage, setErrorMessage] = useState('');

  const layout = graphConfig.layout ? JSON.stringify(trimLayout(graphConfig.layout as ReactGridLayout.Layout)) : null;

  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const saveJSON = (): void => {
    const variables = {
      dashboardName,
      graphIndex,
      graph: json,
      layout,
      tabName,
    };
    saveGraphJSON({ variables })
      .then(() => {
        handleClose();
      })
      .catch((error) => {
        setPageAlertConfig({
          message: clearGraphQLErrorMessage(error.message),
          variant: PageAlertVariant.ERROR,
          autoHide: true,
        });
      });
  };

  // Oy, is CodeMirror ever the old school libaray. Why are we using it again?
  // We have to manually set the focus to get the JSON to show up, as showing
  // this dialog can happen much later than being instantiated. There also
  // doesn't appear to be decent typings, hence the anys.
  let codeMirror = {};
  const onShowDialog = (): void => {
    if (codeMirror) {
      setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const codeMirrorInstance = codeMirror as any;
        codeMirrorInstance.refresh();
        codeMirrorInstance.setSize('100%', '100%');
      }, 1);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onCodeChange = (_editor: any, _data: any, value: string): void => {
    setErrorMessage('');
    setJson(value);
  };

  const onKeyUp = useCallback(
    (_, e): void => {
      const event = e as KeyboardEvent;

      if (event.code === K.KEYBOARD_KEY.ESCAPE) {
        handleClose();
      }
    },
    [handleClose],
  );

  return (
    <Dialog
      title="Edit JSON"
      show={showModal}
      close={(): void => {}}
      noAutoFocus
      onShowCallback={onShowDialog}
      okCallback={saveJSON}
      cancelCallback={handleClose}
    >
      <div className="EditGraphJSON">
        {errorMessage && <div className="EditGraphJSON__SyntaxError">{errorMessage}</div>}
        <CodeEditor
          value={json}
          onChange={onCodeChange}
          onKeyUp={onKeyUp}
          className="EditGraphJSON__CodeEditor"
          options={{ lineNumbers: true, lineWrapping: true }}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          editorInstanceCallback={(editor: any): void => {
            codeMirror = editor;
          }}
        />
      </div>
    </Dialog>
  );
}

type ErrorEditGraphJSONProps = {
  message: string;
  graphIndex: number;
  graphConfig: GraphConfigType;
};

export const ErrorEditGraphJSON = ({ message, graphIndex, graphConfig }: ErrorEditGraphJSONProps): JSX.Element => {
  const [showEditJSON, setShowEditJSON] = useState(false);

  const errorWithButton = {
    message,
    isCompact: true,
    errorLink: {
      text: 'Edit JSON',
      onClick: (): void => {
        setShowEditJSON(true);
      },
    },
  };

  return (
    <>
      <ErrorPlaceholder error={errorWithButton} />
      <EditGraphJSON
        graphConfig={graphConfig}
        showModal={showEditJSON}
        handleClose={(): void => setShowEditJSON(false)}
        graphIndex={graphIndex}
      />
    </>
  );
};

export default EditGraphJSON;
